/**
 * Persona persistence — operator's nick, skills, system prompt,
 * available flag, opted-in tool names, and attached MCP endpoint URLs.
 *
 * Persisted to `localStorage` so a refresh keeps the same identity
 * (and the same MCP attachments) in the mesh roster — without it
 * every reload churns through a new selfId-prefix nick and re-prompts
 * for tool opt-ins.
 *
 * Environment-aware: when `localStorage` isn't available (SSR, web
 * workers, service workers) the accessors silently return the default
 * persona instead of throwing.
 */
const DEFAULT_KEY = 'unstable-legion-persona-v1';
export const DEFAULT_PERSONA = Object.freeze({
    nick: '',
    modelId: 'Qwen2.5-0.5B-Instruct-q4f16_1-MLC',
    available: true,
    skills: ['demo'],
    authoritative: [],
    delegating: [],
    systemPrompt: 'You are a helpful assistant.',
    availableTools: ['current_time', 'ping'],
    mcpEndpoints: [],
    bootMode: 'auto',
});
export function loadPersona(storageKey = DEFAULT_KEY) {
    if (typeof localStorage === 'undefined')
        return DEFAULT_PERSONA;
    try {
        const raw = localStorage.getItem(storageKey);
        if (!raw)
            return DEFAULT_PERSONA;
        const parsed = JSON.parse(raw);
        return {
            nick: typeof parsed.nick === 'string' ? parsed.nick : DEFAULT_PERSONA.nick,
            modelId: typeof parsed.modelId === 'string' ? parsed.modelId : DEFAULT_PERSONA.modelId,
            available: typeof parsed.available === 'boolean' ? parsed.available : DEFAULT_PERSONA.available,
            skills: Array.isArray(parsed.skills) && parsed.skills.every((s) => typeof s === 'string')
                ? parsed.skills
                : DEFAULT_PERSONA.skills,
            systemPrompt: typeof parsed.systemPrompt === 'string' ? parsed.systemPrompt : DEFAULT_PERSONA.systemPrompt,
            availableTools: Array.isArray(parsed.availableTools) &&
                parsed.availableTools.every((s) => typeof s === 'string')
                ? parsed.availableTools
                : DEFAULT_PERSONA.availableTools,
            authoritative: Array.isArray(parsed.authoritative) &&
                parsed.authoritative.every((s) => typeof s === 'string')
                ? parsed.authoritative
                : DEFAULT_PERSONA.authoritative,
            delegating: Array.isArray(parsed.delegating) &&
                parsed.delegating.every((s) => typeof s === 'string')
                ? parsed.delegating
                : DEFAULT_PERSONA.delegating,
            mcpEndpoints: Array.isArray(parsed.mcpEndpoints) &&
                parsed.mcpEndpoints.every((s) => typeof s === 'string')
                ? parsed.mcpEndpoints
                : DEFAULT_PERSONA.mcpEndpoints,
            bootMode: parsed.bootMode === 'fp16' ||
                parsed.bootMode === 'fp32' ||
                parsed.bootMode === 'auto'
                ? parsed.bootMode
                : DEFAULT_PERSONA.bootMode,
        };
    }
    catch {
        return DEFAULT_PERSONA;
    }
}
export function savePersona(p, storageKey = DEFAULT_KEY) {
    if (typeof localStorage === 'undefined')
        return;
    try {
        localStorage.setItem(storageKey, JSON.stringify(p));
    }
    catch {
        /* quota / privacy — silent */
    }
}
